/**
 * Simplified audio configuration for reliable playback
 * Focuses on simple, consistent audio playback without complex Web Audio API
 */

import { devLog } from './utils';

// Simple audio state management - never reset this once unlocked
let audioUnlocked = false;

// Shared audio context for both recording and playback
let sharedAudioContext: AudioContext | null = null;

// Store an audio element created during user gesture for later use
let userGestureAudioElement: HTMLAudioElement | null = null;

/**
 * Get or create the shared audio context
 */
export const getSharedAudioContext = (): AudioContext | null => {
  if (!sharedAudioContext) {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContext) {
        sharedAudioContext = new AudioContext();
        devLog.log('Created shared audio context');
      }
    } catch (error) {
      devLog.warn('Failed to create shared audio context:', error);
    }
  }
  return sharedAudioContext;
};

/**
 * Custom audio recorder using shared context
 */
export interface AudioRecorderState {
  isRecording: boolean;
  recordingBlob: Blob | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  /** 0..1 RMS-derived input level for the active mic stream. Returns 0 when
   *  not recording or when the analyser couldn't be wired up. Safe to poll
   *  at requestAnimationFrame cadence. */
  getMicLevel: () => number;
}

let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let recordingState = {
  isRecording: false,
  recordingBlob: null as Blob | null
};
let analyserNode: AnalyserNode | null = null;
let analyserSource: MediaStreamAudioSourceNode | null = null;
let analyserBuffer: Uint8Array | null = null;

export const createAudioRecorder = (): AudioRecorderState => {
  const startRecording = async (): Promise<void> => {
    try {
      devLog.log('Starting custom audio recording with shared context');
      
      // Clear previous recording
      recordingState.recordingBlob = null;
      
      // Get shared audio context first to ensure it's initialized
      const audioContext = getSharedAudioContext();
      if (audioContext && audioContext.state === 'suspended') {
        await audioContext.resume();
        devLog.log('Resumed shared audio context for recording');
      }
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
        }
      });
      
      // Create MediaRecorder with compatible options
      let options: MediaRecorderOptions = {};
      
      // Try different formats for better compatibility
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options.mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options.mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options.mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        options.mimeType = 'audio/ogg;codecs=opus';
      }
      
      devLog.log(`Using MediaRecorder with mimeType: ${options.mimeType || 'default'}`);

      mediaRecorder = new MediaRecorder(stream, options);
      audioChunks = [];

      // Hook an AnalyserNode off the same MediaStream so the UI can read a
      // live input level (0..1) while recording. Failures are non-fatal —
      // the recorder works either way; getMicLevel() just returns 0.
      try {
        if (audioContext) {
          analyserSource = audioContext.createMediaStreamSource(stream);
          analyserNode = audioContext.createAnalyser();
          analyserNode.fftSize = 1024;
          analyserNode.smoothingTimeConstant = 0.6;
          analyserBuffer = new Uint8Array(analyserNode.fftSize);
          analyserSource.connect(analyserNode);
          // Do NOT connect the analyser to destination — we don't want the
          // mic looped back through the speakers.
        }
      } catch (analyserErr) {
        devLog.warn('Mic level analyser setup failed (non-fatal):', analyserErr);
        analyserSource = null;
        analyserNode = null;
        analyserBuffer = null;
      }
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        devLog.log('MediaRecorder stopped, creating blob');
        
        if (audioChunks.length === 0) {
          devLog.error('No audio chunks recorded');
          recordingState.recordingBlob = null;
          return;
        }
        
        // Create blob with the same MIME type as MediaRecorder
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunks, { type: mimeType });
        
          // Validate blob and use fallback if too small (only in development)
          if ((blob.size === 0 || blob.size < 100)) {
            devLog.warn(`Created blob is too small (${blob.size} bytes)`);
            
            // Only use fallback audio for simulator/testing in development mode
            if (import.meta.env?.DEV) {
              devLog.log('Creating fallback audio for development/simulator');
              createFallbackAudioBlob().then(fallbackBlob => {
                recordingState.recordingBlob = fallbackBlob;
                devLog.log(`Using fallback audio blob for dev: ${fallbackBlob.size} bytes, type: ${fallbackBlob.type}`);
              }).catch(error => {
                devLog.error('Failed to create fallback audio:', error);
                recordingState.recordingBlob = blob; // Use original even if small
              });
            } else {
              devLog.log('Small blob in production - using as-is');
              recordingState.recordingBlob = blob;
            }
            return;
          }
        
        devLog.log(`Created audio blob: ${blob.size} bytes, type: ${blob.type}`);
        recordingState.recordingBlob = blob;
        
        // Tear down the analyser graph before stopping tracks — disconnecting
        // first prevents an "Cannot disconnect a destination that is not
        // connected" warning when the source has already been ended.
        try {
          if (analyserSource) analyserSource.disconnect();
          if (analyserNode) analyserNode.disconnect();
        } catch {
          // ignore
        }
        analyserSource = null;
        analyserNode = null;
        analyserBuffer = null;

        // Clean up stream but keep shared audio context alive
        stream.getTracks().forEach(track => track.stop());
        devLog.log('Recording completed, shared audio context preserved');
      };
      
      // Start recording
      mediaRecorder.start();
      recordingState.isRecording = true;
      devLog.log('Custom recording started successfully with shared context');
      
    } catch (error) {
      devLog.error('Failed to start custom recording:', error);
      throw error;
    }
  };

  const stopRecording = (): void => {
    try {
      devLog.log('Stopping custom audio recording');
      
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        recordingState.isRecording = false;
        devLog.log('Custom recording stopped successfully');
      }
    } catch (error) {
      devLog.error('Failed to stop custom recording:', error);
    }
  };

  const getMicLevel = (): number => {
    if (!analyserNode || !analyserBuffer) return 0;
    try {
      analyserNode.getByteTimeDomainData(analyserBuffer);
      // Compute RMS of the waveform sample. Bytes are centered at 128 with
      // 128 of headroom either side, so we normalise to [-1, 1] before
      // squaring. Multiply the result so quiet-but-audible speech still
      // reads as visible motion in the bars.
      let sumSquares = 0;
      const len = analyserBuffer.length;
      for (let i = 0; i < len; i += 1) {
        const sample = (analyserBuffer[i] - 128) / 128;
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / len);
      // Gentle gain + clamp. RMS of conversational speech sits around 0.05–0.20
      // — multiplying by 4 puts a typical sentence in the 0.2–0.8 range without
      // pinning the meter at 1 on every louder syllable.
      return Math.min(1, rms * 4);
    } catch {
      return 0;
    }
  };

  return {
    get isRecording() { return recordingState.isRecording; },
    get recordingBlob() { return recordingState.recordingBlob; },
    startRecording,
    stopRecording,
    getMicLevel,
  };
};

/**
 * Create and store an audio element during user gesture for later use
 */
export const createUserGestureAudioElement = (): void => {
  try {
    // Create a dummy audio element during the user gesture
    userGestureAudioElement = new Audio();
    userGestureAudioElement.setAttribute('playsinline', 'true');
    userGestureAudioElement.setAttribute('webkit-playsinline', 'true');
    userGestureAudioElement.muted = false;
    userGestureAudioElement.volume = 0.01; // Very low volume
    
    // Try to play a tiny bit of silence to "unlock" this element
    userGestureAudioElement.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7bllHgU2jdXzzn0vBSF+zPLaizsIGGS57OihUBELTKXh8bllHgU2jdXzzn0vBSF+zPLaizsIGGS57OihUBELTKXh8bllHgU2jdXzzn0vBSF+zPLaizsIGGS57OihUBELTKXh8Q==';
    
    // Play and immediately pause to establish the audio context
    userGestureAudioElement.play().then(() => {
      userGestureAudioElement!.pause();
      userGestureAudioElement!.currentTime = 0;
    }).catch(() => {
      // Ignore errors - this is just for unlocking
    });
    
  } catch (error) {
    // Ignore errors - fallback will handle it
  }
};

/**
 * Simple audio unlock - just play a silent audio to unlock the audio system
 * This should be called once on user interaction
 */
export const unlockAudio = async (): Promise<void> => {
  // Always try to unlock - don't trust the state after recording
  devLog.log(`Attempting to unlock audio (current state: ${audioUnlocked})`);

  try {
    devLog.log('Attempting to unlock audio system...');
    
    // Use shared audio context for unlocking
    let audioContext = getSharedAudioContext();
    
    if (audioContext) {
      // Resume context if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        devLog.log('Resumed shared audio context');
      }
      
      // Create a silent tone for unlocking using shared context
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
      
      // Set very low volume and frequency
      gainNode.gain.value = 0.001;
      oscillator.frequency.value = 440;
      
      // Play for a very short duration
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.01);
      
      // Wait for the audio to play
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Don't close the shared context - keep it alive for playback
      
      audioUnlocked = true;
      devLog.log('Audio system unlocked successfully using shared Web Audio API context');
    } else {
      // Fallback: try with an existing audio file from the app
      const audio = new Audio('/sounds/final.mp3');
      
      // Configure for iOS compatibility
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.muted = false;
      audio.volume = 0.01; // Very low volume
      
      // Play and immediately pause to unlock
      await audio.play();
      await new Promise(resolve => setTimeout(resolve, 50));
      audio.pause();
      audio.currentTime = 0;
      
      audioUnlocked = true;
      devLog.log('Audio system unlocked successfully using audio file');
    }
  } catch (error) {
    devLog.warn('Audio unlock failed:', error);
    // Try one more fallback approach
    try {
      devLog.log('Trying fallback audio unlock approach...');
      // Just set the flag and hope for the best
      audioUnlocked = true;
      devLog.log('Audio unlock fallback completed');
    } catch (fallbackError) {
      devLog.error('All audio unlock methods failed:', fallbackError);
    }
  }
};

/**
 * Create a simple audio element with iOS-compatible settings
 */
export const createAudioElement = (src: string): HTMLAudioElement => {
  const audio = new Audio(src);
  
  // Configure for iOS compatibility and silent mode
  audio.setAttribute('playsinline', 'true');
  audio.setAttribute('webkit-playsinline', 'true');
  audio.preload = 'auto';
  audio.muted = false;
  audio.defaultMuted = false;
  
  return audio;
};

/**
 * Simple audio playback with proper error handling.
 *
 * Returns the element that's actually doing the playback. When iOS-style
 * audio unlocking is in effect this is the stored `userGestureAudioElement`,
 * not the `audioElement` argument — callers that watch `currentTime` (e.g.
 * the streaming-text-reveal tracker in `useTrain`) need the real one.
 */
export const playAudio = async (
  audioElement: HTMLAudioElement,
  volume: number = 1.0,
): Promise<HTMLAudioElement> => {
  // First try using the stored user gesture audio element if available
  if (userGestureAudioElement) {
    try {
      devLog.log('Using stored user gesture audio element');

      // Update the stored element with our audio source
      userGestureAudioElement.src = audioElement.src;
      userGestureAudioElement.currentTime = 0;
      userGestureAudioElement.volume = volume;
      userGestureAudioElement.muted = false;

      // Copy event handlers
      userGestureAudioElement.onended = audioElement.onended;
      userGestureAudioElement.onerror = audioElement.onerror;

      await userGestureAudioElement.play();

      // Replace the original element reference with our working one
      Object.setPrototypeOf(audioElement, Object.getPrototypeOf(userGestureAudioElement));
      Object.assign(audioElement, userGestureAudioElement);

      devLog.log('Audio playing successfully using user gesture element');
      return userGestureAudioElement;
    } catch (gestureError) {
      devLog.warn('User gesture element failed, trying fallback:', gestureError);
    }
  }

  // Fallback: try regular playback with unlock
  try {
    await unlockAudio();

    audioElement.currentTime = 0;
    audioElement.volume = volume;
    audioElement.muted = false;

    await audioElement.play();
    devLog.log('Audio playing successfully with fallback method');
    return audioElement;
  } catch (error) {
    devLog.error('Audio playback failed:', error);
    throw error;
  }
};

/**
 * Minimal cleanup for audio elements - don't interfere with audio context
 */
export const cleanupAudioElement = (audioElement: HTMLAudioElement): void => {
  try {
    // Minimal cleanup - just pause and remove handlers
    audioElement.pause();
    audioElement.onended = null;
    audioElement.onerror = null;
    // Don't reset src or currentTime - might interfere with audio context
    devLog.log('Audio element cleaned up (minimal)');
  } catch (error) {
    // Ignore cleanup errors - not critical
  }
};

/**
 * Create a fallback audio blob for development/simulator environments
 * This creates a 5-second WAV audio file with a quiet tone
 * 
 * Used when:
 * - iOS simulator doesn't allow real audio recording
 * - MediaRecorder produces empty/corrupted blobs during development
 * - Only runs in development mode (import.meta.env.DEV)
 */
export const createFallbackAudioBlob = async (): Promise<Blob> => {
  try {
    // Try to create a real audio blob using Web Audio API
    const audioContext = getSharedAudioContext();
    if (audioContext) {
      // Create 5 seconds of silence
      const sampleRate = audioContext.sampleRate;
      const duration = 5; // 5 seconds
      const frameCount = sampleRate * duration;
      
      // Create an offline audio context for rendering
      const offlineContext = new OfflineAudioContext(1, frameCount, sampleRate);
      
      // Create a buffer with silence (or very quiet tone)
      const buffer = offlineContext.createBuffer(1, frameCount, sampleRate);
      const channelData = buffer.getChannelData(0);
      
      // Add very quiet tone so it's not completely silent
      for (let i = 0; i < frameCount; i++) {
        channelData[i] = Math.sin(2 * Math.PI * 440 * i / sampleRate) * 0.001;
      }
      
      const source = offlineContext.createBufferSource();
      source.buffer = buffer;
      source.connect(offlineContext.destination);
      source.start();
      
      // Render the audio
      const renderedBuffer = await offlineContext.startRendering();
      
      // Convert to WAV format (more compatible than WebM for fallback)
      const wavBlob = await audioBufferToWav(renderedBuffer);
      devLog.log(`Created fallback audio blob: ${wavBlob.size} bytes`);
      return wavBlob;
    }
  } catch (error) {
    devLog.warn('Failed to create Web Audio fallback, using minimal blob:', error);
  }
  
  // Fallback: create a minimal WebM-like blob
  const fallbackText = "This is a fallback audio for testing purposes";
  const blob = new Blob([fallbackText], { type: 'audio/webm' });
  devLog.log(`Created minimal fallback blob: ${blob.size} bytes`);
  return blob;
};

/**
 * Convert AudioBuffer to WAV blob
 */
const audioBufferToWav = async (buffer: AudioBuffer): Promise<Blob> => {
  const length = buffer.length;
  const sampleRate = buffer.sampleRate;
  const arrayBuffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * 2, true);
  
  // Convert float samples to 16-bit PCM
  const channelData = buffer.getChannelData(0);
  let offset = 44;
  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, channelData[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
    offset += 2;
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};
